import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-12 px-6 py-4',
        icon: 'h-10 w-10',
        lg: 'h-14 rounded-lg px-10',
        sm: 'h-12 rounded-lg px-6',
      }, 
      variant: {
        default: 'btn bg-gradient-to-r from-accent-2 to-accent btn-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'btn btn-destructive  bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'btn-ghost hover:bg-card hover:text-accent-foreground',
        link: 'btn-link text-primary items-start justify-start underline-offset-4 hover:underline',
        outline: 'btn btn-outline  border border-accent bg-transparent hover:bg-accent text-accent hover:text-foreground',
        carouselOutline: ' border border-border hover:bg-card text-accent hover:text-foreground',
        secondary: 'btn btn-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild = false, className, size, variant, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp data-theme="light" className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
